import type { HomepageSearchResult } from "@search/api/types";
import { SitewideResultTypeEnum } from "@search_v2/enums";
import clsx from "clsx";
import { format } from "date-fns";
import pluralize from "pluralize";
import React, { useCallback, useRef, useState } from "react";

import Button from "@/components/atoms/forms/Button";
import { ButtonActionEnum, ColorEnum } from "@/enums";
import { useScreenSize } from "@/hooks";
import { createNumberOptions } from "@/utils/helpers";

import styles from "./HomepageSearch.module.css";
import Calendar from "./components/Calendar";
import FieldSet from "./components/FieldSet";
import SearchAutocomplete, {
  SearchAutocompleteHandle,
} from "./components/SearchAutocomplete";
import { NavigateToArgs, redirectToResultPage } from "./utils/navigation";

const NIGHTS_OPTIONS = createNumberOptions(1, 10);

interface FormData {
  destination?: string;
  arrivalDate?: Date;
  nights?: string;
}

interface Props {
  fullWidth?: boolean;
  redirectOnSubmit?: boolean;
  onSubmit?: (payload: NavigateToArgs) => void;
}

const HomepageSearch = ({
  fullWidth,
  redirectOnSubmit = true,
  onSubmit,
}: Props) => {
  const { isMaxLg } = useScreenSize();
  const [destination, setDestination] = useState<HomepageSearchResult>();

  const [formData, setFormData] = useState<FormData>({});
  const autocompleteRef = useRef<SearchAutocompleteHandle | null>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const { arrivalDate, nights } = formData;
      const payload = {
        destination,
        ...(arrivalDate && { date: format(arrivalDate, "yyyy-MM-dd") }),
        ...(nights && { nights }),
      };

      if (
        redirectOnSubmit ||
        destination?.type === SitewideResultTypeEnum.VENUE
      ) {
        redirectToResultPage(payload);
        return;
      }
      onSubmit?.(payload);
    },
    [redirectOnSubmit, destination, formData, onSubmit],
  );

  const onSelect = useCallback((item: HomepageSearchResult) => {
    setDestination(item);
  }, []);

  const updateFormData = useCallback(
    <K extends keyof FormData>(key: K, value: FormData[K]) => {
      setFormData((state) => ({ ...state, [key]: value }));
    },
    [],
  );

  const handleNightSelect = useCallback(
    (value: string, close: () => void) => {
      updateFormData("nights", value);
      close();
    },
    [updateFormData],
  );

  return (
    <div className={clsx(styles.homepageSearch, fullWidth && styles.fullWidth)}>
      <form autoComplete="off" onSubmit={handleSubmit}>
        <fieldset className={styles.autocomplete}>
          <SearchAutocomplete
            ref={autocompleteRef}
            noResultsText="No locations found"
            placeholder="Search by keyword"
            label="Where"
            onSelectItem={onSelect}
            onChange={(e) => {
              if (!e.target.value) {
                setDestination(undefined);
              }
            }}
          />
        </fieldset>
        <FieldSet
          title="Arrival Date"
          value={
            formData?.arrivalDate &&
            format(formData?.arrivalDate, "eee, dd MMM yyyy")
          }
          centerPopup
        >
          {({ close }) => (
            <Calendar
              variant={isMaxLg ? "scroll" : "carousel"}
              disabled={{ before: new Date() }}
              onSelectDate={(date) => {
                updateFormData("arrivalDate", date);
                close();
              }}
              stickyDaybar={isMaxLg}
              hideMonthLabel={!isMaxLg}
            />
          )}
        </FieldSet>
        <FieldSet
          title="Length of Stay"
          value={
            formData?.nights &&
            pluralize("night", Number(formData?.nights), true)
          }
        >
          {({ close }) => (
            <ul>
              {NIGHTS_OPTIONS.map(({ value }) => (
                <li
                  key={value}
                  className={clsx(
                    "cursor-pointer rounded-md p-2 hover:bg-gray-100 active:bg-gray-100",
                    formData?.nights === value && "bg-gray-100",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNightSelect(value, close);
                  }}
                >
                  {pluralize("night", Number(value), true)}
                </li>
              ))}
            </ul>
          )}
        </FieldSet>
        <div className={styles.submitBtn}>
          <Button
            color={ColorEnum.JADE}
            type={ButtonActionEnum.SUBMIT}
            fullWidth
          >
            Search
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HomepageSearch;
